'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { DemoEmailRow } from './DemoEmailRow';
import { DemoInbox } from './DemoInbox';
import { cleanInboxEmails, demoFolders, messyInboxEmails } from './demo-data';

const SPAM_COUNT = messyInboxEmails.filter((e) => e.isSpam).length;

interface BeforeAfterDemoProps {
  interactive?: boolean;
  className?: string;
}

export function BeforeAfterDemo({ interactive = false, className = '' }: BeforeAfterDemoProps) {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { amount: 0.3 });
  const prefersReducedMotion = useReducedMotion();
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const sliderPosition = useMotionValue(0);
  const clipPercent = useTransform(sliderPosition, [0, 1], [100, 0]);

  // Extract useTransform calls to component top level (hooks rules)
  const clipPathValue = useTransform(clipPercent, (v) => `inset(0 ${v}% 0 0)`);
  const sliderLeftValue = useTransform(sliderPosition, [0, 1], ['0%', '100%']);

  useEffect(() => {
    if (!isInView || hasAutoPlayed) return;

    // Skip animation for reduced motion — show final state immediately
    if (prefersReducedMotion) {
      sliderPosition.set(1);
      setHasAutoPlayed(true);
      return;
    }

    const duration = 2500;
    const start = performance.now();
    let rafId: number;

    function animate(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Smooth ease-in-out for a more polished feel
      const eased =
        progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      sliderPosition.set(eased);

      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        setHasAutoPlayed(true);
      }
    }

    const timer = setTimeout(() => {
      rafId = requestAnimationFrame(animate);
    }, 800);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(rafId);
    };
  }, [isInView, hasAutoPlayed, sliderPosition, prefersReducedMotion]);

  useEffect(() => {
    if (!isInView) {
      setHasAutoPlayed(false);
      sliderPosition.set(0);
    }
  }, [isInView, sliderPosition]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!interactive || !containerRef.current) return;
      setIsDragging(true);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [interactive]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = Math.max(0, Math.min(1, x / rect.width));
      sliderPosition.set(percent);
    },
    [isDragging, sliderPosition]
  );

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!interactive) return;
      const step = 0.05;
      const current = sliderPosition.get();
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault();
        sliderPosition.set(Math.min(1, current + step));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault();
        sliderPosition.set(Math.max(0, current - step));
      } else if (e.key === 'Home') {
        e.preventDefault();
        sliderPosition.set(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        sliderPosition.set(1);
      }
    },
    [interactive, sliderPosition]
  );

  function getFolderProps(folder?: string) {
    if (!folder) return {};
    const f = demoFolders.find((df) => df.name === folder);
    if (!f) return {};
    return {
      folderLabel: f.name,
      folderColor: f.color,
      folderBgColor: f.bgColor,
      folderBorderColor: f.borderColor,
    };
  }

  return (
    <div
      ref={ref}
      className={className}
      role="img"
      aria-label="Demo: before and after comparison showing a messy inbox cleaned up by filtering cold outreach"
    >
      <div
        ref={containerRef}
        className="relative overflow-hidden rounded-xl"
        onPointerMove={interactive ? handlePointerMove : undefined}
        onPointerUp={interactive ? handlePointerUp : undefined}
      >
        <div className="brightness-[0.85] saturate-[0.6]">
          <DemoInbox label="Before — 8 spam emails mixed in">
            {messyInboxEmails.map((email) => (
              <DemoEmailRow
                key={email.id}
                from={email.from}
                subject={email.subject}
                time={email.time}
              />
            ))}
          </DemoInbox>
        </div>

        <motion.div className="absolute inset-0 bg-background" style={{ clipPath: clipPathValue }}>
          <DemoInbox label="After — clean & organized" filteredCount={SPAM_COUNT}>
            {cleanInboxEmails.map((email) => (
              <DemoEmailRow
                key={email.id}
                from={email.from}
                subject={email.subject}
                time={email.time}
                {...getFolderProps(email.folder)}
              />
            ))}
          </DemoInbox>
        </motion.div>

        <motion.div
          className="absolute top-0 bottom-0 z-10 w-0.5 cursor-ew-resize bg-white/50 shadow-[0_0_8px_rgba(255,255,255,0.3)]"
          style={{ left: sliderLeftValue }}
          onPointerDown={interactive ? handlePointerDown : undefined}
          {...(interactive
            ? {
                role: 'slider',
                tabIndex: 0,
                'aria-label': 'Before and after comparison slider',
                'aria-valuemin': 0,
                'aria-valuemax': 100,
                'aria-valuenow': Math.round(sliderPosition.get() * 100),
                onKeyDown: handleKeyDown,
              }
            : {})}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 p-1.5 backdrop-blur-sm">
            <svg
              className="h-4 w-4 text-white/60"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 3l-3 5 3 5M11 3l3 5-3 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
